/**
 * Environment-aware service origins and clinical-trials endpoint builders.
 *
 * Local and preview builds use the gateway that serves the dashboard; deployed
 * builds use the matching public ingress. Clinical-trials data calls from a
 * local frontend go to the deployed development ingress because that service
 * is what local dashboard work currently talks to.
 */
import { resolveAuthUserUrl, resolveGatewayService } from "../utils/gatewayEnv";
import { getEnv } from "../utils/runtimeEnv";

const CLINICAL_TRIALS_DEV_URL =
  "https://apis-dev.risalabs.ai/clinical-trials-service/";

/**
 * True when a data-plane override is a loopback URL.
 *
 * CRA inlines `.env*` values at webpack start, so a leftover
 * `REACT_APP_CLINICAL_TRIALS_SERVICE_URL=http://localhost:8011/` keeps winning
 * until the process is killed. Local frontend work talks to the deployed
 * development ingress, so loopback overrides are ignored.
 *
 * @param url - Candidate service origin
 * @returns Whether the origin is localhost / 127.0.0.1
 */
function isLoopbackServiceUrl(url: string | undefined): boolean {
  if (!url) return false;
  return /https?:\/\/(localhost|127\.0\.0\.1)(:|\/|$)/i.test(url);
}

const CLINICAL_TRIALS_OVERRIDE = getEnv("REACT_APP_CLINICAL_TRIALS_SERVICE_URL");
const CLINICAL_TRIALS_SERVICE =
  CLINICAL_TRIALS_OVERRIDE && !isLoopbackServiceUrl(CLINICAL_TRIALS_OVERRIDE)
    ? CLINICAL_TRIALS_OVERRIDE
    : resolveGatewayService(
        "clinical-trials-service",
        "https://apis.risalabs.ai/clinical-trials-service/",
        CLINICAL_TRIALS_DEV_URL,
      );

const BACKEND_SERVICE = resolveGatewayService(
  "backend-service",
  "https://apis.risalabs.ai/backend-service/",
  "https://apis-dev.risalabs.ai/backend-service/",
);

// auth/token + auth/verify-totp live on backend-service; auth-wrapper needs both
// AUTH_URL (user-auth) and BACKEND_SERVICE for the full magic-link + TOTP flow.
const AUTH_URL = resolveAuthUserUrl();

export const ApiBaseUrls = {
  AUTH_URL,
  BACKEND_SERVICE,
  CLINICAL_TRIALS_SERVICE,
} as const;

export const API_ENDPOINTS = {
  NOTIFICATIONS: `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/patients/notifications`,
  MARK_NOTIFICATION_READ: `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/patients/notifications/read`,
  MARK_NOTIFICATION_UNREAD: `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/patients/notifications/unread`,
  GET_TAB_COUNTS: `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/patients/notifications/tab-counts`,
  GET_FACETS: `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/patients/notifications/facets`,
  GET_PATIENT_DETAIL: (mrn: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/patients/${encodeURIComponent(mrn)}`,
  UPDATE_PATIENT_TAGS: (mrn: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/patients/${encodeURIComponent(mrn)}/tags`,
  GET_DOCUMENT_SUMMARY: `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/documents/summary`,
  GET_DOCUMENT_PDF: (documentId: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/documents/${encodeURIComponent(documentId)}/pdf`,
  GET_SOURCE_PDF: `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/documents/pdf`,
  RUN_SUMMARIZATION: `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/jobs/run-daily-ingest-for-patients`,
  GET_RUN_HISTORY: `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/jobs/runs`,
  GET_RUN_STATUS: (runId: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/jobs/runs/${encodeURIComponent(runId)}`,
  RESUME_RUN: (runId: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/jobs/runs/${encodeURIComponent(runId)}/resume`,
  GET_RUN_MRNS: (runId: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/jobs/runs/${encodeURIComponent(runId)}/mrns`,
  NOTES: `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/notes`,
  NOTE_ASSIGNEES: `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/notes/assignees`,
  PATIENT_NOTES: (mrn: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/patients/${encodeURIComponent(mrn)}/notes`,
  NOTE_DETAIL: (noteId: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/notes/${encodeURIComponent(noteId)}`,
  NOTE_REPLIES: (noteId: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/notes/${encodeURIComponent(noteId)}/replies`,
  NOTE_REPLY: (noteId: string, activityId: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/notes/${encodeURIComponent(noteId)}/replies/${encodeURIComponent(activityId)}`,
  NOTE_COMMAND: (
    noteId: string,
    command: "resolve" | "reopen" | "read" | "unread",
  ) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/notes/${encodeURIComponent(noteId)}/${command}`,
  ELIGIBILITY_ROWS: `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/eligibility/rows`,
  // Every patient ever screened for a trial, with no date in the request — the CRC
  // worklist is a queue they work down, not a snapshot of one ingest day.
  ELIGIBILITY_SCREENED_ROWS: `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/eligibility/screened-rows`,
  ELIGIBILITY_OPTIONS: `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/eligibility/options`,
  ELIGIBILITY_PATCH: (trialId: string, mrn: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/eligibility/${encodeURIComponent(trialId)}/${encodeURIComponent(mrn)}`,
  ELIGIBILITY_ANALYZE: (trialId: string, mrn: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/eligibility/${encodeURIComponent(trialId)}/${encodeURIComponent(mrn)}/analyze`,
  ELIGIBILITY_ANALYZE_MANY: (trialId: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/eligibility/${encodeURIComponent(trialId)}/analyze`,
  ELIGIBILITY_SCREENING: (trialId: string, mrn: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/eligibility/${encodeURIComponent(trialId)}/${encodeURIComponent(mrn)}/screening`,
  ELIGIBILITY_DETAIL: (trialId: string, mrn: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/eligibility/${encodeURIComponent(trialId)}/${encodeURIComponent(mrn)}/detail`,
  ELIGIBILITY_CRITERIA_DETAIL: (trialId: string, mrn: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/eligibility/${encodeURIComponent(trialId)}/${encodeURIComponent(mrn)}/criteria-detail`,
  ELIGIBILITY_BOOKMARK: (trialId: string, mrn: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/eligibility/${encodeURIComponent(trialId)}/${encodeURIComponent(mrn)}/bookmark`,
  ELIGIBILITY_READ_STATE: (trialId: string, mrn: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/eligibility/${encodeURIComponent(trialId)}/${encodeURIComponent(mrn)}/read-state`,
  ELIGIBILITY_NOTIFICATIONS: (trialId: string, mrn: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/eligibility/${encodeURIComponent(trialId)}/${encodeURIComponent(mrn)}/notifications`,
  ELIGIBILITY_NOTIFICATION_READ: (
    trialId: string,
    mrn: string,
    notificationId: string,
  ) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/eligibility/${encodeURIComponent(trialId)}/${encodeURIComponent(mrn)}/notifications/${encodeURIComponent(notificationId)}/read`,
  ELIGIBILITY_NOTIFICATIONS_CLEAR: (trialId: string, mrn: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/eligibility/${encodeURIComponent(trialId)}/${encodeURIComponent(mrn)}/notifications/clear`,
  ELIGIBILITY_STATUS_HISTORY: (trialId: string, mrn: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/eligibility/${encodeURIComponent(trialId)}/${encodeURIComponent(mrn)}/status-history`,
  ELIGIBILITY_MONITORING_TRIALS: (trialId: string, mrn: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/eligibility/${encodeURIComponent(trialId)}/${encodeURIComponent(mrn)}/monitoring-trials`,
  ELIGIBILITY_ATOM_OVERRIDE: (trialId: string, mrn: string, atomId: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/eligibility/${encodeURIComponent(trialId)}/${encodeURIComponent(mrn)}/overrides/${encodeURIComponent(atomId)}`,
  ELIGIBILITY_OVERRIDE_JUSTIFICATION_UPLOAD: (
    trialId: string,
    mrn: string,
    actionId: string,
  ) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/eligibility/${encodeURIComponent(trialId)}/${encodeURIComponent(mrn)}/recommended-actions/${encodeURIComponent(actionId)}/justification`,
  ELIGIBILITY_OVERRIDE_INTENT: (
    trialId: string,
    mrn: string,
    actionId: string,
  ) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/eligibility/${encodeURIComponent(trialId)}/${encodeURIComponent(mrn)}/recommended-actions/${encodeURIComponent(actionId)}/override`,
  ELIGIBILITY_CREATE_TASK: (
    trialId: string,
    mrn: string,
    actionId: string,
  ) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/eligibility/${encodeURIComponent(trialId)}/${encodeURIComponent(mrn)}/recommended-actions/${encodeURIComponent(actionId)}/task`,
  AUTHENTICATED_CLINICAL_TRIALS_PATH: (relativePath: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}${relativePath.replace(/^\/+/, "")}`,
  TRIALS: `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/trials`,
  TRIAL_DETAIL: (trialId: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/trials/${encodeURIComponent(trialId)}`,
  TRIAL_ARCHIVE: (trialId: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/trials/${encodeURIComponent(trialId)}/archive`,
  TRIAL_RESTORE: (trialId: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/trials/${encodeURIComponent(trialId)}/restore`,
  TRIAL_IMPORT_PREVIEW: (trialId: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/trials/${encodeURIComponent(trialId)}/imports/preview`,
  TRIAL_IMPORT_CONFIRM: (trialId: string, importId: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/trials/${encodeURIComponent(trialId)}/imports/${encodeURIComponent(importId)}/confirm`,
  TRIAL_DRAFT: (trialId: string, draftId: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/trials/${encodeURIComponent(trialId)}/protocol-drafts/${encodeURIComponent(draftId)}`,
  TRIAL_DRAFT_CRITERIA: (trialId: string, draftId: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/trials/${encodeURIComponent(trialId)}/protocol-drafts/${encodeURIComponent(draftId)}/criteria`,
  TRIAL_DRAFT_VALIDATE: (trialId: string, draftId: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/trials/${encodeURIComponent(trialId)}/protocol-drafts/${encodeURIComponent(draftId)}/validate`,
  TRIAL_DRAFT_SUGGESTIONS: (trialId: string, draftId: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/trials/${encodeURIComponent(trialId)}/protocol-drafts/${encodeURIComponent(draftId)}/connection-suggestions`,
  TRIAL_DRAFT_REVIEW: (
    trialId: string,
    draftId: string,
    action:
      "request-review" | "request-changes" | "approve" | "publish" | "discard",
  ) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/trials/${encodeURIComponent(trialId)}/protocol-drafts/${encodeURIComponent(draftId)}/${action}`,
  TRIAL_DRAFT_CONNECTIONS: (trialId: string, draftId: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/trials/${encodeURIComponent(trialId)}/protocol-drafts/${encodeURIComponent(draftId)}/connections`,
  TRIAL_DRAFT_CONNECTION_DELETE: (
    trialId: string,
    draftId: string,
    connectionId: string,
  ) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/trials/${encodeURIComponent(trialId)}/protocol-drafts/${encodeURIComponent(draftId)}/connections/${encodeURIComponent(connectionId)}/delete`,
  TRIAL_VERSIONS: (trialId: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/trials/${encodeURIComponent(trialId)}/protocol-versions`,
  TRIAL_AUDIT: (trialId: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/trials/${encodeURIComponent(trialId)}/audit`,
  TRIAL_T1_RUNS: (trialId: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/trials/${encodeURIComponent(trialId)}/t1-runs`,
  TRIAL_T1_RUN: (trialId: string, runId: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/trials/${encodeURIComponent(trialId)}/t1-runs/${encodeURIComponent(runId)}`,
  TRIAL_T1_FUNNEL: (trialId: string, runId: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/trials/${encodeURIComponent(trialId)}/t1-runs/${encodeURIComponent(runId)}/funnel`,
  TRIAL_T1_PATIENTS: (trialId: string, runId: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/trials/${encodeURIComponent(trialId)}/t1-runs/${encodeURIComponent(runId)}/patients`,
  TRIAL_T1_PATIENT: (trialId: string, runId: string, mrn: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/trials/${encodeURIComponent(trialId)}/t1-runs/${encodeURIComponent(runId)}/patients/${encodeURIComponent(mrn)}`,

  // Agentic screening. These proxy to the agent-orchestration service, which has
  // no ingress of its own — clinical-trials authorises the superadmin and calls it
  // with a service identity, so the browser only ever talks to this origin.
  SCREENING_WORKFLOWS: `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/screening/workflows`,
  SCREENING_WORKFLOW: (name: string, version?: number) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/screening/workflows/${encodeURIComponent(name)}${version ? `?version=${version}` : ""}`,
  SCREENING_RUNS: `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/screening/runs`,
  SCREENING_RUN: (runId: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/screening/runs/${encodeURIComponent(runId)}`,
  SCREENING_RUN_RESULTS: (runId: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/screening/runs/${encodeURIComponent(runId)}/results`,
  SCREENING_RUN_LOGS: (runId: string, item?: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/screening/runs/${encodeURIComponent(runId)}/logs${item ? `?item=${encodeURIComponent(item)}` : ""}`,
  // Cohort screening: one multipart CSV of MRNs queues a whole cohort against a trial.
  SCREENING_BATCH: (trialId: string) =>
    `${ApiBaseUrls.CLINICAL_TRIALS_SERVICE}api/v1/screening/batch/${encodeURIComponent(trialId)}`,
} as const;
